import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trophy, BarChart3, Pencil, Trash2, ChevronRight } from "lucide-react";
import { api } from "@/lib/handball";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import AppHeader from "@/components/AppHeader";
import TournamentDialog from "@/components/TournamentDialog";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function safeFormatDate(value) {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    return format(d, "d MMM yyyy", { locale: sv });
  } catch {
    return "";
  }
}

/**
 * TournamentsPage — top-level "Turneringar / Serier" lane:
 *  - Lists all tournaments the user can see (owned + where their GKs have sessions)
 *  - Create new tournament
 *  - Click → open aggregated per-GK comparison view
 */
export default function TournamentsPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, listRes] = await Promise.all([
        api.get("/auth/me").catch(() => ({ data: null })),
        api.get("/tournaments", { params: { scope: "all" } }),
      ]);
      setMe(meRes.data);
      setTournaments(listRes.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const active = tournaments.filter((t) => t.end_date >= today);
  const past = tournaments.filter((t) => t.end_date < today);
  const myId = me?.id;

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/tournaments/${deleteTarget.id}`);
      toast.success("Turnering borttagen");
      setDeleteTarget(null);
      await load();
    } catch {
      toast.error("Kunde inte ta bort turneringen");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <AppHeader subtitle="Turneringar / Serier" backTo="/" backLabel="Startsida" />
      <main className="max-w-[1100px] mx-auto px-6 py-10 space-y-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-2">
              Säsongsöversikt
            </div>
            <h1 className="font-display font-black uppercase tracking-widest text-3xl md:text-4xl text-white">
              Turneringar &amp; Serier
            </h1>
            <div className="text-zinc-400 mt-2">
              Dina egna turneringar plus de där dina målvakter har spelat.
            </div>
          </div>
          <Button
            data-testid="tournaments-new-btn"
            onClick={() => {
              setEditTarget(null);
              setDialogOpen(true);
            }}
            className="h-12 rounded-none bg-[#FFD60A] hover:bg-[#eac200] text-black font-display uppercase tracking-widest"
          >
            <Plus size={16} className="mr-2" />
            Ny turnering/serie
          </Button>
        </div>

        {loading ? (
          <div className="text-zinc-500 font-display uppercase tracking-widest">
            Laddar…
          </div>
        ) : tournaments.length === 0 ? (
          <div
            className="card-tactical p-16 text-center"
            data-testid="tournaments-empty"
          >
            <Trophy size={32} className="text-[#FFD60A] mx-auto mb-4" />
            <div className="font-display font-black text-xl uppercase tracking-widest mb-2">
              Inga turneringar ännu
            </div>
            <div className="text-zinc-400 mb-6">
              Skapa en för att samla matcher under en gemensam säsong eller cup.
            </div>
            <Button
              data-testid="tournaments-empty-new"
              onClick={() => {
                setEditTarget(null);
                setDialogOpen(true);
              }}
              className="rounded-none bg-[#FFD60A] hover:bg-[#eac200] text-black font-display uppercase tracking-widest"
            >
              <Plus size={16} className="mr-2" />
              Skapa den första
            </Button>
          </div>
        ) : (
          <>
            {active.length > 0 && (
              <TournamentGroup
                title="Pågående"
                data-testid-group="tournaments-active"
                tournaments={active}
                myId={myId}
                onOpen={(t) => navigate(`/tournaments/${t.id}`)}
                onEdit={(t) => { setEditTarget(t); setDialogOpen(true); }}
                onDelete={(t) => setDeleteTarget(t)}
              />
            )}
            {past.length > 0 && (
              <TournamentGroup
                title="Avslutade"
                data-testid-group="tournaments-past"
                tournaments={past}
                myId={myId}
                onOpen={(t) => navigate(`/tournaments/${t.id}`)}
                onEdit={(t) => { setEditTarget(t); setDialogOpen(true); }}
                onDelete={(t) => setDeleteTarget(t)}
              />
            )}
          </>
        )}
      </main>

      <TournamentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode={editTarget ? "edit" : "create"}
        tournament={editTarget}
        onSaved={load}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent
          data-testid="tournament-delete-confirm"
          className="bg-[#111] border-[#27272A] text-white rounded-none"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-widest text-xl">
              Ta bort turnering?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              <span className="block text-white font-display tracking-wide mb-2">
                {deleteTarget?.name}
              </span>
              Matcherna behålls men lämnar turneringen. Ingen skottdata försvinner.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel className="rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white font-display uppercase tracking-widest">
              Avbryt
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={confirmDelete}
              className="rounded-none bg-[#FF3B30] hover:bg-[#e33228] text-white font-display uppercase tracking-widest"
            >
              {deleting ? "Tar bort…" : "Ja, ta bort"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TournamentGroup({ title, tournaments, myId, onOpen, onEdit, onDelete }) {
  return (
    <section>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-3">
        {title}
      </div>
      <div className="space-y-2">
        {tournaments.map((t) => {
          const owned = t.owner_id === myId;
          return (
            <div
              key={t.id}
              data-testid={`tournament-row-${t.id}`}
              className="flex items-center gap-2 border border-[#27272A] hover:border-[#FFD60A] transition-colors p-4"
            >
              <button
                type="button"
                onClick={() => onOpen(t)}
                className="flex-1 min-w-0 text-left"
                data-testid={`tournament-open-${t.id}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <Trophy size={16} className="text-[#FFD60A]" />
                  <span className="font-display uppercase tracking-widest text-white text-lg">
                    {t.name}
                  </span>
                  {!owned && (
                    <span className="text-[9px] font-bold tracking-widest border border-[#3F3F46] text-zinc-400 px-1.5 py-0.5">
                      SKAPAD AV ANNAN
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  {safeFormatDate(t.start_date + "T00:00:00") || t.start_date}
                  {" — "}
                  {safeFormatDate(t.end_date + "T00:00:00") || t.end_date}
                </div>
              </button>
              <button
                type="button"
                data-testid={`tournament-eval-${t.id}`}
                onClick={() => onOpen(t)}
                className="h-10 px-3 border border-[#27272A] hover:bg-[#171717] text-xs font-display uppercase tracking-widest text-white"
                title="Öppna utvärdering"
              >
                <BarChart3 size={14} />
              </button>
              {owned && (
                <>
                  <button
                    type="button"
                    data-testid={`tournament-edit-${t.id}`}
                    onClick={() => onEdit(t)}
                    className="h-10 px-3 border border-[#27272A] hover:bg-[#171717] text-xs text-zinc-400 hover:text-white"
                    title="Redigera"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    data-testid={`tournament-delete-${t.id}`}
                    onClick={() => onDelete(t)}
                    className="h-10 px-3 border border-[#27272A] text-[#FF3B30] hover:bg-[#FF3B30]/10"
                    title="Ta bort"
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              )}
              <ChevronRight
                size={16}
                className="text-zinc-600 hidden sm:block"
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}
